import * as Sentry from '@sentry/react';

// Lives at the `@myosh/sentry/capture` sub-path (not the package root) because
// it binds to `@sentry/react`: the root entry stays SDK-agnostic so the React
// Native app can keep importing it without pulling the browser SDK into its
// bundle.

type CaptureContext = Parameters<typeof Sentry.captureException>[1];

/**
 * `Sentry.captureException` for shared code that may run in a host which never
 * initialised Sentry — the mobile WebView build, unit tests, or local dev with
 * no DSN. Reporting is best-effort by design: it must never break the flow it
 * is instrumenting.
 *
 * `captureException` synthesises a truthy event id even with no client, so its
 * return value can't tell us whether the event went anywhere — gate on
 * `getClient()` instead (same idiom as the web unhandled-rejection chokepoint).
 *
 * @returns `true` when the event was handed to an initialised client.
 */
export function captureIfInitialised(error: unknown, context?: CaptureContext): boolean {
  try {
    if (!Sentry.getClient()) {
      return false;
    }
    Sentry.captureException(error, context);
    return true;
  } catch {
    return false;
  }
}
